import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer, resetMasterCache, useMasterOptions, withNulls } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton, Tabs } from "../hrComponents";
import { cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";

// ORG-01/02: the organisation masters. One screen serves all six; each entry lists its
// columns and form fields (mirroring the API's masters.config.js).
const TZ = [{ label: "Asia/Dubai", value: "Asia/Dubai" }, { label: "Asia/Kolkata", value: "Asia/Kolkata" }];
const COUNTRY = [{ label: "UAE", value: "AE" }, { label: "India", value: "IN" }];

export const MASTERS = [
  {
    key: "departments",
    label: "Departments",
    noun: "department",
    columns: [["Name", (r) => r.name], ["Code", (r) => r.code], ["Head", (r) => r.head_employee_name]],
    fields: (opt) => [
      { key: "name", label: "Name", required: true, max: 100 },
      { key: "code", label: "Code", max: 20 },
      { key: "parent_id", label: "Parent department", type: "select", options: opt.departments || [] },
      { key: "head_employee_id", label: "Department head", type: "employee" },
    ],
    needs: ["departments"],
  },
  { key: "designations", label: "Designations", noun: "designation", columns: [["Name", (r) => r.name]], fields: () => [{ key: "name", label: "Name", required: true, max: 100 }], needs: [] },
  {
    key: "grades",
    label: "Grades",
    noun: "grade",
    columns: [["Name", (r) => r.name], ["Band", (r) => r.band], ["Rank", (r) => r.rank_no]],
    fields: () => [
      { key: "name", label: "Name", required: true, max: 100 },
      { key: "band", label: "Band", max: 20 },
      { key: "rank_no", label: "Rank (lower is more senior)", type: "number", min: 0 },
    ],
    needs: [],
  },
  {
    key: "locations",
    label: "Locations",
    noun: "location",
    columns: [["Name", (r) => r.name], ["Address", (r) => r.address], ["Country", (r) => r.country_code]],
    // Country matches the tenant's own HR country (locked, not a free choice); the India-only state code
    // (for PT/LWF) only shows for India tenants.
    fields: (opt, country) => [
      { key: "name", label: "Name", required: true, max: 100 },
      { key: "address", label: "Address", max: 255 },
      { key: "country_code", label: "Country", type: "select", options: COUNTRY, disabled: true },
      { key: "timezone", label: "Timezone", type: "select", options: TZ },
      ...(country === "IN" ? [{ key: "state_code", label: "State code (India PT / LWF)", max: 8 }] : []),
      { key: "latitude", label: "Latitude", type: "number", step: "any" },
      { key: "longitude", label: "Longitude", type: "number", step: "any" },
      { key: "geofence_radius_m", label: "Geofence radius (metres)", type: "number", min: 10 },
    ],
    needs: [],
  },
  {
    key: "sites",
    label: "Sites",
    noun: "site",
    columns: [["Name", (r) => r.name], ["Client", (r) => r.client_name]],
    fields: (opt) => [
      { key: "name", label: "Name", required: true, max: 100 },
      { key: "client_name", label: "Client", max: 150 },
      { key: "location_id", label: "Location", type: "select", options: opt.locations || [] },
      { key: "incharge_employee_id", label: "Site in-charge", type: "employee" },
      { key: "latitude", label: "Latitude", type: "number", step: "any" },
      { key: "longitude", label: "Longitude", type: "number", step: "any" },
      { key: "geofence_radius_m", label: "Geofence radius (metres)", type: "number", min: 10 },
    ],
    needs: ["locations"],
  },
  {
    key: "cost-centres",
    label: "Cost centres",
    noun: "cost centre",
    columns: [["Code", (r) => r.code], ["Name", (r) => r.name]],
    fields: () => [
      { key: "code", label: "Code", required: true, max: 20 },
      { key: "name", label: "Name", required: true, max: 100 },
    ],
    needs: [],
  },
];

const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

function MasterList({ master }) {
  const [showInactive, setShowInactive] = useState(false);
  const { data, loading, error, reload } = useLoad(() => hrmsService.listMaster(master.key), [master.key]);
  const settings = useLoad(() => hrmsService.getSettings(), []);
  const country = settings.data?.country_pack;
  const [edit, setEdit] = useState(null); // { row }
  const options = useMasterOptions(master.needs.length ? master.needs : ["departments"]);
  const rows = (data || []).filter((r) => showInactive || r.status === "ACTIVE");

  const changed = () => {
    resetMasterCache();
    reload();
  };
  const setStatus = async (row) => {
    const res =
      row.status === "ACTIVE"
        ? await hrmsService.deactivateMaster(master.key, row.id).catch((e) => ({ success: false, message: e.message }))
        : await hrmsService.saveMaster(master.key, row.id, { status: "ACTIVE" });
    if (res?.success) {
      toast.success(res.message || "Saved");
      changed();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        <PrimaryButton onClick={() => setEdit({ row: null })} disabled={master.key === "locations" && settings.loading}>
          Add {master.noun}
        </PrimaryButton>
        <div className="form-check mb-0">
          <input id={`inactive-${master.key}`} type="checkbox" className="form-check-input" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          <label htmlFor={`inactive-${master.key}`} className="form-check-label" style={{ fontSize: 13 }}>
            Show inactive
          </label>
        </div>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title={`No ${master.label.toLowerCase()} yet`} />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {master.columns.map(([h]) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                  <th style={thStyle}>Status</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    {master.columns.map(([h, get]) => (
                      <td key={h} style={tdStyle}>
                        {get(r) ?? "—"}
                      </td>
                    ))}
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit({ row: r })}>
                        Edit
                      </button>{" "}
                      <button type="button" style={linkBtn} onClick={() => setStatus(r)}>
                        {r.status === "ACTIVE" ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={!!edit}
        title={`${edit?.row ? "Edit" : "Add"} ${master.noun}`}
        fields={master.fields(options, country)}
        initial={edit?.row || (master.key === "locations" && country ? { country_code: country } : {})}
        onHide={() => setEdit(null)}
        onSave={(_clean, raw) => hrmsService.saveMaster(master.key, edit?.row?.id, withNulls(raw, master.fields(options, country).map((f) => f.key)))}
        onDone={changed}
      />
    </>
  );
}

export default function OrganisationTab() {
  const [key, setKey] = useState(MASTERS[0].key);
  const master = MASTERS.find((m) => m.key === key);
  return (
    <>
      <Tabs tabs={MASTERS.map((m) => ({ key: m.key, label: m.label }))} active={key} onChange={setKey} />
      <MasterList key={key} master={master} />
    </>
  );
}
